import {ConfirmationDialog} from '@/components/ui/confirmation-dialog-atoms/ConfirmationDialogAtoms'
import {DialogHeading} from '@/components/ui/dialog-atoms/DialogAtoms'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Text} from '@/components/ui/text/Text'
import dayjs, {formatLocaleDate} from '@/dayjs'
import {TransformProjectFormValidationSchema} from '@/features/projects/formSchemas'
import {useStoreRenewTransformProject} from '@/features/projects/queries/useStoreRenewTransformProject'
import {assistiveRegimeToRehabCareCommitment} from '@/features/projects/utils'
import {QUERY_KEYS, queryClient} from '@/queryClient'
import {useTranslation} from '@/translations/i18n'
import {routes} from '@/utilities/constants/routes'
import {toast} from '@/utilities/toast/toast'
import {DialogStore} from '@ariakit/react'
import {useFormContext, useWatch} from 'react-hook-form'
import {generatePath, useNavigate} from 'react-router-dom'

export const TransformProjectConfirmationModal = ({
    dialogStore,
    projectId
}: {
    dialogStore: DialogStore
    projectId: string
}) => {
    const {handleSubmit, control} = useFormContext<TransformProjectFormValidationSchema>()
    const patient = useWatch({control, name: 'patient'})

    const {t} = useTranslation()
    const navigate = useNavigate()

    const {mutate: transformProject, isPending} = useStoreRenewTransformProject({
        projectId: projectId,
        options: {
            onSuccess: project => {
                void queryClient.cancelQueries({queryKey: [QUERY_KEYS.PROJECTS]})
                void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECTS]})
                toast.success(
                    <Flexbox direction="column">
                        <Text size="textSm" weight="500">
                            {t('projects:project_created')}
                        </Text>
                        {project.patient.fullName && (
                            <Text size="textSm" palette="neutral" shade="500">
                                {project.patient.fullName}
                            </Text>
                        )}
                    </Flexbox>
                )

                // The transformation creates a new project: land on its detail page, not on the projects list.
                navigate(generatePath(routes.PROJECT_DETAILS_PROJECT_DATA.handle.path, {id: project.id}), {
                    replace: true
                })
            },
            onError: error => {
                console.error('Renew project error: ', error)
                dialogStore.setOpen(false)
            }
        }
    })

    const handleConfirmSubmit = handleSubmit(formValues => {
        transformProject({
            action: 'transform',
            empty: formValues.siarProject == 'empty',
            type: 'continuous',
            sector: formValues.sector,
            assistiveRegime: formValues.assistiveRegime || null,
            startDate: formatLocaleDate(formValues.startDate, 'YYYY-MM-DD'),
            expirationDate: formatLocaleDate(formValues.expirationDate, 'YYYY-MM-DD'),
            durationDays: formValues.durationDays || dayjs(formValues.startDate).diff(formValues.expirationDate, 'day'),
            sender: formValues.sender,
            specialistId: formValues.specialist.id,
            // This is sent to the backend just to allow the SIAR export, it is not a field of our form
            rehabilitativeAssistanceCommitment:
                formValues.sector == 'outpatient' && formValues.assistiveRegime
                    ? assistiveRegimeToRehabCareCommitment[formValues.assistiveRegime].toString()
                    : undefined
        })
    })

    return (
        <ConfirmationDialog
            dialogStore={dialogStore}
            onConfirm={handleConfirmSubmit}
            isConfirmPending={isPending}
            headerSlot={
                <Flexbox direction="column">
                    <DialogHeading>{t('projects:transform_project')}</DialogHeading>
                    <Text palette="neutral" shade="500" size="textSm">
                        {t('commons:patient')}: {patient.fullName}
                    </Text>
                </Flexbox>
            }
        />
    )
}
